<template>
  <v-row>
    <v-col cols="12" md="12">
      <v-card>
        <v-card-title class="d-flex align-center">
          Select Date Range:
          <DateRangePicker
            :ranges="ranges"
            v-model="dateRange"
            opens="right"
            :singleDatePicker="false"
            :maxDate="new Date()"
            @update="debounceFetching"
          >
            <template v-slot:input="picker">
              {{ picker.startDate | date }} - {{ picker.endDate | date }}
            </template>
          </DateRangePicker>
        </v-card-title>
      </v-card>
    </v-col>
    <v-col cols="12" md="6">
      <v-card>
        <v-row>
          <v-col cols="12" md="6">
            <v-row>
              <v-list class="ml-5">
                <v-list-item>
                  <v-chip color="gray" text-color="black">
                    <v-skeleton-loader v-if="loading" type="text" width="150px">
                    </v-skeleton-loader>
                    <template v-else>
                      Total:
                      {{ calculatedTotalEmailsByStatus }}
                    </template>
                  </v-chip>
                </v-list-item>
                <v-list-item
                  v-for="(amount, status) in statusTotals"
                  :key="status"
                >
                  <v-chip
                    :color="
                      SENDGRID_EMAIL_STATUS.find((p) => p.id === status).color
                    "
                    text-color="white"
                  >
                    <v-skeleton-loader v-if="loading" type="text" width="150px">
                    </v-skeleton-loader>
                    <template v-else>
                      {{ status.charAt(0).toUpperCase() + status.slice(1) }}:
                      {{ amount }} ({{
                        (
                          (amount / calculatedTotalEmailsByStatus) *
                          100
                        ).toFixed(2)
                      }}%)
                    </template>
                  </v-chip>
                </v-list-item>
              </v-list>
            </v-row>
          </v-col>
          <v-col cols="12" md="6">
            <PieChartStatus :chartData="emailsByStatusData" />
          </v-col>
        </v-row>
      </v-card>
    </v-col>
    <v-col cols="12" md="6">
      <v-card>
        <v-row>
          <v-col cols="12" md="6">
            <v-list class="ml-5">
              <v-list-item>
                <v-chip color="gray" text-color="black">
                  <v-skeleton-loader v-if="loading" type="text" width="150px">
                  </v-skeleton-loader>
                  <template v-else>
                    Total:
                    {{ calculatedTotalEmailsByType }}
                  </template>
                </v-chip>
              </v-list-item>
              <v-list-item v-for="(amount, type) in typeTotals" :key="type">
                <v-chip
                  :color="EMAIL_TYPES.find((p) => p.id === type).color"
                  text-color="white"
                >
                  <v-skeleton-loader v-if="loading" type="text" width="150px">
                  </v-skeleton-loader>
                  <template v-else>
                    {{ EMAIL_TYPES.find((p) => p.id === type).name }}
                    : {{ amount }} ({{
                      ((amount / calculatedTotalEmailsByType) * 100).toFixed(2)
                    }}%)
                  </template>
                </v-chip>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="12" md="6">
            <PieChartType :chartData="emailsByTypeData" />
          </v-col>
        </v-row>
      </v-card>
    </v-col>
    <v-col cols="12" md="12">
      <v-card>
        <LineChartStatus :lineChartData="emailsByStatusData" />
      </v-card>
    </v-col>
    <v-col cols="12" md="12">
      <v-card>
        <LineChartType :lineChartData="emailsByTypeData" />
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import { API } from "../../api";
import LineChartStatus from "./LineChartStatus.vue";
import PieChartStatus from "./PieChartStatus.vue";
import PieChartType from "./PieChartType.vue";
import LineChartType from "./LineChartType.vue";
import { SENDGRID_EMAIL_STATUS, EMAIL_TYPES } from "../../constants";

export default {
  name: "EmailsChart",
  components: {
    DateRangePicker,
    LineChartStatus,
    PieChartStatus,
    PieChartType,
    LineChartType,
  },
  data() {
    return {
      SENDGRID_EMAIL_STATUS,
      EMAIL_TYPES,
      emailsByStatusData: [],
      emailsByTypeData: [],
      dateRange: {
        startDate: new Date(new Date().getTime() - 8 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
      },
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
        previousPage: 0,
      },
      loading: false,
    };
  },
  filters: {
    date(date) {
      if (!date) return "";
      return date.toISOString().substr(0, 10).split("-").reverse().join(".");
    },
  },
  computed: {
    calculatedTotalEmailsByStatus() {
      return this.emailsByStatusData.reduce(
        (total, item) => total + item.count,
        0
      );
    },
    calculatedTotalEmailsByType() {
      return this.emailsByTypeData.reduce(
        (total, item) => total + item.count,
        0
      );
    },
    ranges() {
      const startOfDay = new Date(new Date().setHours(2, 0, 0, 0));
      const endOfDay = new Date(new Date().setHours(23, 59, 59, 999));

      const thisMonthStart = new Date(
        startOfDay.getFullYear(),
        startOfDay.getMonth(),
        1
      );
      const thisMonthEnd = new Date(
        endOfDay.getFullYear(),
        endOfDay.getMonth() + 1,
        0
      );

      return {
        Today: [startOfDay, endOfDay],
        "Last 7 days": [
          new Date(
            startOfDay.getFullYear(),
            startOfDay.getMonth(),
            startOfDay.getDate() - 7
          ),
          endOfDay,
        ],
        "Last 30 days": [
          new Date(
            startOfDay.getFullYear(),
            startOfDay.getMonth(),
            startOfDay.getDate() - 30
          ),
          endOfDay,
        ],
        "This month": [thisMonthStart, thisMonthEnd],
        "This Year": [
          new Date(startOfDay.getFullYear(), 0, 1),
          new Date(endOfDay.getFullYear(), 11, 31),
        ],
        "Last month": [
          new Date(startOfDay.getFullYear(), startOfDay.getMonth() - 1, 1),
          new Date(endOfDay.getFullYear(), endOfDay.getMonth(), 0),
        ],
        "Last Year": [
          new Date(startOfDay.getFullYear() - 1, 0, 1),
          new Date(endOfDay.getFullYear() - 1, 11, 31),
        ],
      };
    },
    statusTotals() {
      const totals = this.emailsByStatusData.reduce((acc, item) => {
        const status = item._id.status;
        if (!acc[status]) {
          acc[status] = 0;
        }
        acc[status] += item.count;
        return acc;
      }, {});

      // Sort the platforms by review amount in descending order
      return Object.fromEntries(
        Object.entries(totals).sort(([, a], [, b]) => b - a)
      );
    },
    typeTotals() {
      const totals = this.emailsByTypeData.reduce((acc, item) => {
        const type = item._id.type;
        if (!acc[type]) {
          acc[type] = 0;
        }
        acc[type] += item.count;
        return acc;
      }, {});

      return Object.fromEntries(
        Object.entries(totals).sort(([, a], [, b]) => b - a)
      );
    },
  },
  created() {
    this.getEmails();
  },
  methods: {
    debounceFetching() {
      if (this.timerId) {
        clearTimeout(this.timerId);
      }

      this.timerId = setTimeout(() => {
        this.getEmails();
      }, 500);
    },
    async getEmails() {
      this.loading = true;

      try {
        const { getEmailsByStatus, getEmailsByType } =
          await API.getEmailsStatistics({
            ...this.dateRange,
            scopes: "getEmailsByStatus,getEmailsByType",
          });

        // Set value if undefined
        this.emailsByStatusData = getEmailsByStatus.map((x) => {
          if (!x._id.status) {
            x._id.status = "sending";
          }
          return x;
        });

        this.emailsByTypeData = getEmailsByType;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
